import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {queryOptions, useSuspenseQuery, type DataTag} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {reactFetch} from '@github-ui/verified-fetch'
import {usePageDataContext} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import type {HeaderPageData} from '../payloads/header'
import type {MainQueryResult} from '@github-ui/react-core/future/main-query'

export function buildHeaderPageDataQueryKey(basePageDataUrl: string) {
  return [PageData.header, `basePageDataURL:${basePageDataUrl}`] as DataTag<string[], MainQueryResult<HeaderPageData>>
}

function useHeaderQueryKey() {
  const {basePageDataUrl} = usePageDataContext()
  return buildHeaderPageDataQueryKey(basePageDataUrl)
}

function useHeaderPageDataQueryOptions() {
  const queryKey = useHeaderQueryKey()
  const apiURL = usePageDataUrl(PageData.header)

  return queryOptions({
    queryKey,
    queryFn: async () => {
      const result = await reactFetch(apiURL)
      if (!result.ok) throw new Error(`HTTP ${result.status}`)
      const json = await result.json()
      const mainQueryData: MainQueryResult<HeaderPageData> = {payload: json}
      return mainQueryData
    },
    staleTime: Infinity,
    select: data => data.payload,
  })
}

function useSuspenseHeaderQuery(initialData?: HeaderPageData) {
  return useSuspenseQuery({
    ...useHeaderPageDataQueryOptions(),
    initialData: initialData && {payload: initialData},
  })
}

export function useLayoutQueryKey() {
  const isLayoutRouteQueryEnabled = isFeatureEnabled('prs_layout_route_query')

  // The flag won't change during the app lifecycle so we can safely conditionally call the hook, which is important
  // because if we call `useRouteQueryKey` without a router context then the app will crash. Hopefully we don't do
  // that, but guarding against that is the whole point of flagging this change. The downside is that the conditional
  // hook breaks react compiler, so we'll want to clean this up soon to get the performance optimization back.
  const queryKey = isLayoutRouteQueryEnabled
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useRouteQueryKey(pullRequestsLayoutRoute, 'mainQuery')
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useHeaderQueryKey()

  if (!queryKey) throw new Error('Pull requests layout route is not mounted')
  return queryKey
}

export function useLayoutQuery(initialData?: HeaderPageData) {
  const isLayoutRouteQueryEnabled = isFeatureEnabled('prs_layout_route_query')
  return isLayoutRouteQueryEnabled
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useRouteQuery(pullRequestsLayoutRoute, 'mainQuery', {initialData})
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useSuspenseHeaderQuery(initialData)
}
